"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { UIMessage } from "ai";
import { skipToken } from "@tanstack/react-query";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ExternalLink, MessageCircle, RotateCcw, X } from "lucide-react";
import { ChatArea } from "~/app/asistent/chat-area";
import { Button, buttonVariants } from "~/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";

const WIDGET_CONVERSATION_COOKIE = "assistant_widget_conversation_id";
const WIDGET_CONVERSATION_MAX_AGE_SECONDS = 24 * 60 * 60;
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const VISIBLE_EXACT_PATHS = [
  "/",
  "/brzo-zakazivanje",
  "/diskretna-i-sigurna-terapija",
  "/fleksibilni-termini",
  "/iskusni-psiholozi",
  "/iskustva",
  "/o-nama",
  "/partneri",
  "/pretraga",
  "/pristupacna-cena-psihoterapije",
  "/psiholozi-na-srpskom",
  "/terapija/za-parove",
  "/za-kompanije",
  "/za-psihoterapeute",
];
const VISIBLE_PATH_PREFIXES = [
  "/podrska",
  "/terapeuti",
  "/terapija",
  "/testovi",
];
const HIDDEN_PATH_PREFIXES = [
  "/admin",
  "/app/asistent",
  "/app/assistant",
  "/app/login",
  "/app/session",
  "/asistent",
  "/device-test",
  "/video-call-test",
];

function isTherapistBookingPath(pathname: string) {
  return /^\/terapeuti\/[^/]+\/book(?:\/|$)/.test(pathname);
}

function isQuizResultPath(pathname: string) {
  return /^\/testovi\/[^/]+\/[^/]+$/.test(pathname);
}

export function shouldShowAssistantWidget(pathname: string | null) {
  if (!pathname) {
    return false;
  }

  if (isTherapistBookingPath(pathname) || isQuizResultPath(pathname)) {
    return false;
  }

  const isHidden = HIDDEN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  if (isHidden) {
    return false;
  }

  if (VISIBLE_EXACT_PATHS.includes(pathname)) {
    return true;
  }

  return VISIBLE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function readWidgetConversationCookie() {
  if (typeof document === "undefined") {
    return null;
  }

  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${WIDGET_CONVERSATION_COOKIE}=`));

  if (!cookie) {
    return null;
  }

  const value = cookie.split("=")[1];

  return value ? decodeURIComponent(value) : null;
}

function writeWidgetConversationCookie(conversationId: string) {
  const secure = window.location.protocol === "https:" ? "Secure" : "";

  document.cookie = [
    `${WIDGET_CONVERSATION_COOKIE}=${encodeURIComponent(conversationId)}`,
    `Max-Age=${WIDGET_CONVERSATION_MAX_AGE_SECONDS}`,
    "Path=/",
    "SameSite=Lax",
    secure,
  ]
    .filter(Boolean)
    .join("; ");
}

function clearWidgetConversationCookie() {
  document.cookie = `${WIDGET_CONVERSATION_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function AssistantWidget() {
  const pathname = usePathname();
  // The widget lives in the root layout, so on cached/PPR pages its server
  // render is baked into a static shell where usePathname() is not the URL the
  // visitor is on (Vercel's ISR regeneration renders the homepage shell with a
  // different pathname than the browser sees). Any pathname-dependent output
  // in the initial render therefore hydrates against stale HTML and throws
  // React #418. Rendering nothing until after mount keeps the hydration
  // render deterministic; visibility is applied in the first client-only
  // render and on every client-side navigation.
  const [hasMounted, setHasMounted] = useState(false);
  const [overlayMode, setOverlayMode] = useState<"mobile" | "desktop" | null>(
    null,
  );
  const [restoreConversationId, setRestoreConversationId] = useState<
    string | null
  >(null);
  const [open, setOpen] = useState(false);
  const [chatKey, setChatKey] = useState(0);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [hasRestoredConversation, setHasRestoredConversation] = useState(false);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const visible = hasMounted && shouldShowAssistantWidget(pathname);
  const restoreQueryInput =
    restoreConversationId && !hasRestoredConversation
      ? { id: restoreConversationId }
      : skipToken;
  const { data: restoredConversation, isFetched: hasFetchedRestore } =
    api.assistant.getWidgetConversation.useQuery(restoreQueryInput);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!visible || hasRestoredConversation) {
      return;
    }

    setRestoreConversationId(readWidgetConversationCookie());
  }, [hasRestoredConversation, visible]);

  useEffect(() => {
    if (!visible || typeof document === "undefined") {
      return;
    }

    setPageTitle(document.title || null);
  }, [pathname, visible]);

  const pageContext = useMemo(() => {
    if (!pathname) {
      return null;
    }

    return {
      pathname,
      title: pageTitle,
    };
  }, [pageTitle, pathname]);

  const handleNewConversation = useCallback(() => {
    clearWidgetConversationCookie();
    setRestoreConversationId(null);
    setHasRestoredConversation(true);
    setMessages([]);
    setConversationId(null);
    setInput("");
    setChatKey((key) => key + 1);
  }, []);

  const handleOpen = useCallback(() => {
    const nextOverlayMode =
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_MEDIA_QUERY).matches
        ? "mobile"
        : "desktop";

    capturePostHogEvent("ai_assistant_widget_opened", {
      page_path: pathname,
      overlay_mode: nextOverlayMode,
    });
    setOverlayMode(nextOverlayMode);
    setOpen(true);
  }, [pathname]);

  const handleConversationStateChange = useCallback(
    (state: { conversationId: string | null; messages: UIMessage[] }) => {
      setConversationId(state.conversationId);
      setMessages(state.messages);

      if (state.conversationId) {
        writeWidgetConversationCookie(state.conversationId);
      }
    },
    [],
  );

  useEffect(() => {
    if (!restoreConversationId || !hasFetchedRestore) {
      return;
    }

    setHasRestoredConversation(true);

    if (!restoredConversation) {
      clearWidgetConversationCookie();
      setRestoreConversationId(null);
      return;
    }

    setConversationId(restoredConversation.id);
    setMessages(restoredConversation.messages as UIMessage[]);
    setChatKey((key) => key + 1);
  }, [hasFetchedRestore, restoredConversation, restoreConversationId]);

  if (!visible) {
    return null;
  }

  const trigger = <AssistantWidgetTrigger onClick={handleOpen} />;

  return (
    <>
      {trigger}
      {overlayMode === "mobile" && (
        <Drawer open={open} onOpenChange={setOpen} repositionInputs={false}>
          <DrawerContent className="h-[88svh] max-h-[calc(100svh-env(safe-area-inset-top)-0.75rem)] overflow-hidden p-0">
            <div className="flex h-full min-h-0 flex-col">
              <DrawerHeader className="shrink-0 border-b px-4 py-3 text-left">
                <div className="flex items-center justify-between gap-3 pr-1">
                  <DrawerTitle className="text-base">Podrška</DrawerTitle>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="Novi razgovor"
                      onClick={handleNewConversation}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <DrawerClose asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Zatvori asistenta"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </DrawerClose>
                  </div>
                </div>
              </DrawerHeader>
              <AssistantWidgetChat
                chatKey={chatKey}
                overlayMode={overlayMode}
                messages={messages}
                conversationId={conversationId}
                input={input}
                pageContext={pageContext}
                onNewConversation={handleNewConversation}
                onConversationStateChange={handleConversationStateChange}
                onInputChange={setInput}
                inputAutoFocus={false}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}
      {overlayMode === "desktop" && (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="right"
            className="flex h-dvh w-[min(92vw,520px)] flex-col gap-0 p-0 sm:max-w-none"
          >
            <SheetHeader className="shrink-0 border-b px-4 py-3 pr-12 text-left">
              <div className="flex items-center justify-between gap-3">
                <SheetTitle className="text-base">Podrška</SheetTitle>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Novi razgovor"
                    onClick={handleNewConversation}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Link
                    href="/asistent"
                    className={cn(
                      buttonVariants({ variant: "ghost", size: "icon" }),
                      "h-8 w-8",
                    )}
                    aria-label="Otvori asistenta preko cele stranice"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </SheetHeader>
            <AssistantWidgetChat
              chatKey={chatKey}
              overlayMode={overlayMode}
              messages={messages}
              conversationId={conversationId}
              input={input}
              pageContext={pageContext}
              onNewConversation={handleNewConversation}
              onConversationStateChange={handleConversationStateChange}
              onInputChange={setInput}
              inputAutoFocus
            />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

function AssistantWidgetTrigger({ onClick }: { onClick: () => void }) {
  return (
    <>
      <button
        type="button"
        data-attr="assistant-widget-trigger"
        onClick={onClick}
        className="fixed bottom-[calc(env(safe-area-inset-bottom)+6.25rem)] right-4 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-background/95 text-brand shadow-md shadow-black/10 backdrop-blur-xs transition hover:bg-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:hidden"
        aria-label="Pitaj AI asistenta"
      >
        <MessageCircle className="h-5 w-5" />
      </button>

      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-attr="assistant-widget-trigger"
              onClick={onClick}
              className="fixed bottom-5 right-5 z-40 hidden h-11 items-center justify-center gap-2 rounded-full bg-brand-action px-4 text-sm font-medium text-brand-foreground shadow-lg shadow-black/15 transition hover:bg-brand-action-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:inline-flex"
              aria-label="Pitaj AI asistenta"
            >
              <MessageCircle className="h-5 w-5" />
              <span>Pitaj asistenta</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Pitaj AI asistenta</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  );
}

function AssistantWidgetChat({
  chatKey,
  overlayMode,
  messages,
  conversationId,
  input,
  pageContext,
  onNewConversation,
  onConversationStateChange,
  onInputChange,
  inputAutoFocus,
}: {
  chatKey: number;
  overlayMode: "mobile" | "desktop";
  messages: UIMessage[];
  conversationId: string | null;
  input: string;
  pageContext: { pathname: string; title?: string | null } | null;
  onNewConversation: () => void;
  onConversationStateChange: (state: {
    conversationId: string | null;
    messages: UIMessage[];
  }) => void;
  onInputChange: (input: string) => void;
  inputAutoFocus: boolean;
}) {
  const handleUserMessageSubmitted = useCallback(() => {
    capturePostHogEvent("ai_assistant_widget_message_sent", {
      page_path: pageContext?.pathname,
      overlay_mode: overlayMode,
      has_existing_conversation: Boolean(conversationId),
    });
  }, [conversationId, overlayMode, pageContext?.pathname]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <ChatArea
        key={chatKey}
        onNewConversation={onNewConversation}
        initialMessages={messages}
        initialConversationId={conversationId}
        onConversationStateChange={onConversationStateChange}
        initialInput={input}
        onInputChange={onInputChange}
        onUserMessageSubmitted={handleUserMessageSubmitted}
        pageContext={pageContext}
        hideHeader
        fullWidthDesktop
        disclosurePlacement="inline"
        inputAutoFocus={inputAutoFocus}
        emptyStateLayout="compact"
      />
    </div>
  );
}
